import { Form, Input } from 'antd';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

// title mapping กลาง ใช้ร่วมกันทุกไฟล์ (เคยก็อปวางซ้ำในเกือบทุกไฟล์)
export const TITLE_TH_LIST = ['นาย', 'นาง', 'นางสาว', 'เด็กหญิง', 'เด็กชาย'];
export const TITLE_EN_LIST = ['Mr', 'Mrs', 'Miss', 'Miss', 'Master'];
export const TITLE_SPECIAL_TH_LIST = ['นายแพทย์', 'แพทย์หญิง', 'ทันตแพทย์ชาย', 'ทันตแพทย์หญิง', 'เภสัชกรหญิง', 'เภสัชกรชาย'];
export const TITLE_SPECIAL_EN_LIST = ['DR.(Male)', 'DR.(Female)', 'D.D.S.(Male)', 'D.D.S.(Female)', 'female pharmacist', 'male pharmacist'];

export const titleEnFromTh = (title_th) => TITLE_EN_LIST[TITLE_TH_LIST.indexOf(title_th)];
export const titleThFromEn = (title_en) => TITLE_TH_LIST[TITLE_EN_LIST.indexOf(title_en)];
export const specialTitleEnFromTh = (title_special_th) =>
  TITLE_SPECIAL_EN_LIST[TITLE_SPECIAL_TH_LIST.indexOf(title_special_th)];

// ---- helper สำหรับ payload ของ One ID (ใช้ร่วมกันทั้ง oneId / oneIdPMUC / oneIdRA) ----
const NON_THAI = /[^ก-๛\s]+/g;
const NON_ENG = /[^A-Za-z\s]+/g;
const TITLE_TH = /[^ก-๛\s.]+/g;
const TITLE_ENG = /[^A-Za-z\s.]+/g;
const clean = (v, re) => (v ? String(v).replace(re, '') : null);
const strip = (v, chars = [' ']) =>
  v == null || v === '' ? null : chars.reduce((s, ch) => s.replaceAll(ch, ''), String(v));
const trimmed = (v) => (v == null || v === '' ? null : String(v).trim());
const contractNo = (r, objective) => (objective.includes('เลขที่หนังสือสัญญา') ? r.contract_no : null);

const buildOneIdBase = (r, { business_id, objective }) => ({
  special_title_th: clean(r.special_title_th, TITLE_TH),
  special_title_eng: clean(r.special_title_eng, TITLE_ENG),
  account_title_th: clean(r.title_th, NON_THAI),
  first_name_th: clean(r.name_th, NON_THAI),
  last_name_th: clean(r.surname_th, NON_THAI),
  account_title_eng: clean(titleEnFromTh(r.title_th), NON_ENG),
  first_name_eng: clean(r.name_eng, NON_ENG),
  last_name_eng: clean(r.surname_eng, NON_ENG),
  id_card_num: strip(r.cid, [' ', '-']),
  email: strip(r.email),
  biz_email: strip(r.biz_email),
  mobile_no: strip(r.mobile || r.tel),
  username: strip(r.username),
  employee_id: r.employee_id,
  biz_id: business_id,
  objective,
});

/**
 * Config ต่อประเภท pre-register หนึ่งตัว = หนึ่ง endpoint + วิธี build payload
 * เพิ่มประเภทใหม่ = เพิ่ม entry ใหม่ในนี้ ไม่ต้องเขียน callAgain ใหม่
 *
 * field เสริมสำหรับ generic UI (ตอนนี้เต็มเฉพาะ thaid / thaidGeneralForm — ตัวอื่นยังไม่ migrate):
 *  - fields: schema ของ field ที่ใช้ derive columns/validator/form ทั้งระบบ
 *  - group: ใช้จับกลุ่มประเภทที่อยู่หน้าเดียวกัน (เช่น thaid, thaidGeneralForm อยู่กลุ่ม 'thaid')
 *  - serviceKey: string เดียวกับที่ใช้เช็ค services array และใช้เป็นค่า objective
 *  - label / pageTitle / entityLabel / primarySearchField / searchPlaceholder / templateFile / listEndpointPath
 *    ใช้แทน hardcode ที่เคยกระจายอยู่ในแต่ละไฟล์ (ThaID.jsx, AddThaID.jsx, ExcelImport, ฯลฯ)
 */
export const preRegisterTypes = {
  // ครู / อาจารย์
  eduTeacher: {
    objectiveMatch: (objective) => objective.includes('edu-teacher-pre-register'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/education/teacher/pre-register`,
    buildPayload: (request, { business_id }) => ({
      account_title_eng: titleEnFromTh(request.title_th),
      title_en: titleEnFromTh(request.title_th),
      special_title_eng: specialTitleEnFromTh(request.title_special_th),
      biz_id: business_id,
      ...request,
    }),
  },

  // นักเรียน / นักศึกษา
  eduStudent: {
    objectiveMatch: (objective) => objective.includes('edu-student-pre-register'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/education/student/pre-register`,
    buildPayload: (request, { business_id }) => {
      const title_en = request.title_en || titleEnFromTh(request.title_th);
      const title_th = request.title_th || titleThFromEn(request.title_en);
      return { account_title_eng: title_en, title_en, title_th, biz_id: business_id, ...request };
    },
  },

  // เจ้าหน้าที่ (officer)
  eduOfficer: {
    objectiveMatch: (objective) => objective.includes('edu-officer-pre-register'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/education/officer/pre-register`,
    buildPayload: (request, { business_id }) => {
      const title_en = request.title_en || titleEnFromTh(request.title_th);
      const title_th = request.title_th || titleThFromEn(request.title_en);
      return { account_title_eng: title_en, title_en, title_th, biz_id: business_id, ...request };
    },
  },

  // บุคลากรทางการแพทย์ (doctor / Pre-register)
  doctorPreregister: {
    objectiveMatch: (objective) => objective.includes('Pre-register'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/doctor/preregister`,
    buildPayload: (request, { business_id, objective }) => ({
      employee_id: request.employee_id.toString(),
      account_title_th: request.title_th,
      special_title_th: request.title_special_th,
      first_name_th: request.name_th,
      last_name_th: request.surname_th,
      account_title_eng: titleEnFromTh(request.title_th),
      special_title_eng: specialTitleEnFromTh(request.title_special_th),
      first_name_eng: request.name_eng,
      last_name_eng: request.surname_eng,
      id_card_num: request.cid.toString(),
      email: request.email,
      mobile_no: request.mobile ?? request.tel,
      biz_id: business_id,
      address: request.address,
      position: request.position,
      contract_no: objective.includes('เลขที่หนังสือสัญญา') ? request.contract_no : null,
    }),
  },

  thaid: {
    key: 'thaid',
    group: 'thaid',
    serviceKey: 'thaid-pre-register',
    label: 'ThaID',
    pageTitle: 'จัดการข้อมูลรอลงทะเบียนด้วย ThaID',
    entityLabel: 'ThaID',
    primarySearchField: 'cid',
    searchPlaceholder: 'ค้นหาด้วยเลขบัตรประจำตัวประชาชน',
    templateFile: 'thaid-pre-register.xlsx',
    listEndpointPath: 'thaid-pre-register',
    priority: 1,
    objectiveMatch: (objective) =>
      objective.includes('thaid-pre-register') && !objective.includes('thaid-pre-register-form-general'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v7/thaid/pre-register`,
    fields: [
      { key: 'cid', label: 'เลขประจำตัวประชาชน', required: true, width: 200, type: 'text' },
      {
        key: 'id_card_type', label: 'ประเภทบัตร', required: true, width: 180, type: 'select',
        options: ['ID_CARD', 'PASSPORT'],
      },
    ],
    buildPayload: (request, { business_id, file_id }) => ({
      biz_id: business_id,
      file_id: file_id,
      pre_register_data: [
        {
          cid: request.cid
            ? request.cid.toString().replaceAll(' ', '').replaceAll('-', '')
            : null,
          id_card_type: request.id_card_type,
        },
      ],
    }),
  },

  thaidGeneralForm: {
    key: 'thaidGeneralForm',
    group: 'thaid',
    serviceKey: 'thaid-pre-register-form-general',
    label: 'ThaID (General Form)',
    pageTitle: 'จัดการข้อมูลรอลงทะเบียนด้วย ThaID',
    entityLabel: 'ThaID (General Form)',
    primarySearchField: 'cid',
    searchPlaceholder: 'ค้นหาด้วยเลขบัตรประจำตัวประชาชน',
    templateFile: 'thaid-pre-register-form-general.xlsx',
    listEndpointPath: 'thaid-pre-register',
    priority: 2,
    objectiveMatch: (objective) => objective.includes('thaid-pre-register-form-general'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v7/thaid/pre-register`,
    fields: [
      { key: 'cid', label: 'เลขประจำตัวประชาชน', required: true, width: 200, type: 'text' },
      {
        key: 'id_card_type', label: 'ประเภทบัตร', required: true, width: 180, type: 'select',
        options: ['ID_CARD', 'PASSPORT'],
      },
      { key: 'mobile_no', label: 'เบอร์โทร', required: false, width: 150, type: 'text', responseKey: 'mobileNO' },
      { key: 'email', label: 'อีเมล', required: false, width: 200, type: 'text' },
      { key: 'department', label: 'แผนก', required: false, width: 150, type: 'text' },
      { key: 'position', label: 'ตำแหน่ง', required: false, width: 150, type: 'text' },
    ],
    buildPayload: (request, { business_id, file_id }) => ({
      biz_id: business_id,
      file_id: file_id,
      pre_register_data: [
        {
          cid: request.cid
            ? request.cid.toString().replaceAll(' ', '').replaceAll('-', '')
            : null,
          id_card_type: request.id_card_type,
          mobile_no: request.mobile_no ? request.mobile_no.toString().replaceAll(' ', '') : null,
          email: request.email ? request.email.toString().replaceAll(' ', '') : null,
          department: request.department ?? null,
          position: request.position ?? null,
        },
      ],
    }),
  },

  // One ID แบบ PMUC (objective 'one-id-pmuc')
  // okMessages = response.message ที่ถือว่า "สำเร็จ" แม้ HTTP error (มีอยู่ใน business แล้ว) -> ติด is_duplicate
  oneIdPMUC: {
    objectiveMatch: (objective) => objective.includes('one-id-pmuc'),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/create-account-oneid-pmuc`,
    okMessages: ['This user is already in the business.'],
    buildPayload: (r, ctx) => ({
      ...buildOneIdBase(r, ctx),
      subject: 'pmuc',
      contract_no: contractNo(r, ctx.objective),
      department: strip(r.department),
      position: strip(r.position),
      address: strip(r.address),
      sub_organization: strip(r.sub_organization),
    }),
  },

  // One ID แบบ RA — เช็คจาก objective 'one-id-ra' (หน้า history) หรือ objectiveCheck 'ra' + 'one-id' (หน้า add)
  oneIdRA: {
    objectiveMatch: (objective, objectiveCheck = []) =>
      objective.includes('one-id-ra') || (objectiveCheck.includes('ra') && objective.includes('one-id')),
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/create-account-oneid-ra`,
    okMessages: ['Account already have this Dept Role.'],
    buildPayload: (r, ctx) => ({
      ...buildOneIdBase(r, ctx),
      file_id: ctx.file_id,
      subject: 'ra',
      objective: ctx.objective.includes('one-id-ra') ? ctx.objective : [...ctx.objective, 'one-id-ra'],
      position: strip(r.position, [' ', '-']),
      department: strip(r.department, [' ', '-']),
    }),
  },

  // ค่าเริ่มต้น / fallback: One ID ทั่วไป (CA, import-excel-rama ฯลฯ)
  oneId: {
    objectiveMatch: () => true, // ใช้เป็นตัวสุดท้ายเสมอ (fallback)
    endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/v5/create-account-oneid`,
    okMessages: ['This user is already in the business.'],
    buildPayload: (r, ctx) => ({
      ...buildOneIdBase(r, ctx),
      department: trimmed(r.department),
      position: trimmed(r.position),
      subject:
        ctx.objective.includes('import-excel-rama') || ctx.services?.includes('import-excel-rama') ? 'rama' : 'pmuc',
      contract_no: contractNo(r, ctx.objective),
    }),
  },
};

/**
 * เลือก config ที่ตรงกับ objective ปัจจุบัน
 * ลำดับการเช็คตรงกับ if/else เดิมในทุกไฟล์ (doctor > student > teacher > officer > thaidGeneralForm > thaid > oneIdPMUC > oneIdRA > oneId)
 * thaidGeneralForm ต้องมาก่อน thaid เสมอ เพราะ objective ของ general-form มี substring
 * 'thaid-pre-register' ปนอยู่ด้วย (thaid.objectiveMatch กันเคสนี้ไว้แล้วด้วย แต่ลำดับก็ยังสำคัญ)
 */
export function resolvePreRegisterConfig(objective, objectiveCheck = []) {
  const order = [
    'doctorPreregister', 'eduStudent', 'eduTeacher', 'eduOfficer',
    'thaidGeneralForm', 'thaid', 'oneIdPMUC', 'oneIdRA', 'oneId',
  ];
  for (const key of order) {
    const config = preRegisterTypes[key];
    if (config.objectiveMatch(objective, objectiveCheck)) return config;
  }
  return preRegisterTypes.oneId;
}

// เช็คว่า objective นี้เป็น ThaID general-form หรือไม่ — ใช้ร่วมกันทุกไฟล์ที่เกี่ยวกับ ThaID
export const isThaIDGeneralForm = (objective) =>
  objective.some((o) => o.includes('thaid-pre-register-form-general'));

// เช็คจาก services ของ business (ค่าเสถียร ไม่ผูกกับ dropdown ที่ user เลือกตอน import)
// ต่างจาก isThaIDGeneralForm ที่เช็คจาก objective (ผูกกับฟอร์มที่กำลังใช้ import ขณะนั้น)
export const isThaIDGeneralFormService = (services) =>
  services.includes('thaid-pre-register-form-general');

// ---- Helpers ที่ derive จาก group metadata (ใช้กับหน้า generic ที่เลือกฟอร์มผ่าน dropdown ได้) ----

export const getGroupConfigs = (groupKey) =>
  Object.values(preRegisterTypes).filter((c) => c.group === groupKey);

export const getAvailableGroupConfigs = (groupKey, services) =>
  getGroupConfigs(groupKey).filter((c) => services.includes(c.serviceKey));

// ---- Helpers ที่ derive จาก fields schema (ใช้แทน hardcode ทุกไฟล์) ----

// แทน validator object ที่เคย hardcode ใน ExcelImport.jsx
export const buildExcelValidator = (config) =>
  Object.fromEntries(config.fields.map((f) => [f.key, f.label]));

// แทน form.setFieldsValue({...}) ที่เคย hardcode field list ใน edit()
export const buildEmptyFormValues = (config) =>
  Object.fromEntries(config.fields.map((f) => [f.key, '']));

// แทน columns array ที่เคยเขียนเองทุกไฟล์ — ใช้ได้ทั้ง editable/read-only
// editable: true ต้องส่ง isEditing, data, setData มาด้วย (select type ต้องอัปเดต state ตรงๆ)
export const buildFieldColumns = (config, { editable = false, isEditing, data, setData } = {}) =>
  config.fields.map(({ key, label, width, type, options }) => ({
    title: label,
    dataIndex: key,
    key,
    width,
    responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
    render: (_, record) => {
      if (!editable) return record[key] || '-';

      const rowEditing = isEditing(record);
      if (!rowEditing) return record[key];

      if (type === 'select') {
        return (
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id={key}>{label}</InputLabel>
            <Select
              value={record[key]}
              labelId={key}
              label={label}
              onChange={(e) => {
                const tempData = [...data];
                const idx = tempData.findIndex((item) => item.key === record.key);
                if (idx > -1) {
                  tempData[idx] = { ...tempData[idx], [key]: e.target.value };
                  setData(tempData);
                }
              }}
            >
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      return (
        <Form.Item name={key} style={{ margin: 0 }}>
          <Input />
        </Form.Item>
      );
    },
  }));